import {
  html,
  type ReactiveController,
  type ReactiveControllerHost,
  type TemplateResult,
} from "lit";
import { cn } from "@/utilities/css";
import { NPS_SCORES, type FeedbackSurveySubmitDetail } from "./types";

interface NpsFeedbackCallbacks {
  onCancel: () => void;
  onSubmit: (detail: FeedbackSurveySubmitDetail) => void;
}

export class NpsFeedbackController implements ReactiveController {
  private _npsFeedback = "";
  private _npsRating: number | undefined = undefined;

  private _onFeedbackInput = (e: CustomEvent<{ value: string }>) => {
    // No requestUpdate: nothing rendered depends on the feedback text (the
    // textarea holds its own value), so re-rendering on every keystroke is waste.
    if (typeof e.detail?.value === "string") {
      this._npsFeedback = e.detail.value;
    }
  };

  private _onSubmit = () => {
    // === undefined not falsy: 0 is a valid NPS score
    if (this._npsRating === undefined) return;
    this.callbacks.onSubmit({
      feedback: this._npsFeedback,
      feedbackType: "nps",
      rating: this._npsRating,
    });
  };

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private callbacks: NpsFeedbackCallbacks
  ) {
    host.addController(this);
  }

  private _onRatingKeydown(e: KeyboardEvent, score: number) {
    const ratings = Array.from(
      this.host.shadowRoot!.querySelectorAll<HTMLElement>(".eko-feedback-survey__nps-rating")
    );
    const index = NPS_SCORES.indexOf(score);

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight": {
        e.preventDefault();
        const nextIndex = (index + 1) % ratings.length;
        this._npsRating = NPS_SCORES[nextIndex];
        this.host.requestUpdate();
        this.host.updateComplete.then(() => ratings[nextIndex].focus());
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        e.preventDefault();
        const prevIndex = (index - 1 + ratings.length) % ratings.length;
        this._npsRating = NPS_SCORES[prevIndex];
        this.host.requestUpdate();
        this.host.updateComplete.then(() => ratings[prevIndex].focus());
        break;
      }
      case " ":
      case "Enter":
        e.preventDefault();
        this._npsRating = score;
        this.host.requestUpdate();
        break;
    }
  }

  private _renderRating(score: number): TemplateResult {
    const selected = this._npsRating === score;
    const tabindex = selected || (this._npsRating === undefined && score === 0) ? 0 : -1;
    const label =
      score === 0
        ? "0 - Not at all likely"
        : score === 10
          ? "10 - Extremely likely"
          : String(score);
    return html`
      <div
        class=${cn("eko-feedback-survey__nps-rating", {
          "eko-feedback-survey__nps-rating--selected": selected,
        })}
        role="radio"
        aria-checked=${String(selected)}
        aria-label=${label}
        tabindex=${tabindex}
        @click=${() => {
          this._npsRating = score;
          this.host.requestUpdate();
        }}
        @keydown=${(e: KeyboardEvent) => this._onRatingKeydown(e, score)}>
        ${score}
      </div>
    `;
  }

  private _resolvePlaceholder(
    negative: string | undefined,
    neutral: string | undefined,
    positive: string | undefined
  ): string {
    if (this._npsRating !== undefined) {
      if (this._npsRating <= 6 && negative) return negative;
      if ([7, 8].includes(this._npsRating) && neutral) return neutral;
      if (this._npsRating >= 9 && positive) return positive;
    }
    return "Provide feedback (optional)";
  }

  // Required: ReactiveController's hooks are all optional, so a controller that
  // implements none of them fails TypeScript's weak-type check at addController.
  hostConnected() {}

  render(params: {
    followUpNegative: string | undefined;
    followUpNeutral: string | undefined;
    followUpPositive: string | undefined;
    loading: boolean;
    question: string;
  }): TemplateResult {
    const { followUpNegative, followUpNeutral, followUpPositive, loading, question } = params;

    const placeholder = this._resolvePlaceholder(
      followUpNegative,
      followUpNeutral,
      followUpPositive
    );

    return html`
      <div class="eko-feedback-survey__body">
        <div class="eko-feedback-survey__nps-ratings" role="radiogroup" aria-label=${question}>
          ${NPS_SCORES.map((s) => this._renderRating(s))}
        </div>
        <eko-textarea
          placeholder=${placeholder}
          .value=${this._npsFeedback}
          .rows=${3}
          @input=${this._onFeedbackInput}></eko-textarea>
      </div>

      <div class="eko-feedback-survey__footer">
        <eko-button-group variant="equal">
          <eko-button ?disabled=${loading} @click=${this.callbacks.onCancel}> Not now </eko-button>
          <eko-button
            variant="primary"
            ?disabled=${this._npsRating === undefined || loading}
            ?loading=${loading}
            @click=${this._onSubmit}>
            Send
          </eko-button>
        </eko-button-group>
      </div>
    `;
  }

  reset() {
    this._npsRating = undefined;
    this._npsFeedback = "";
  }
}
